import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import CommonFields from "../../common/CommonField";
import {
  Grid,
  Column,
  FormGroup,
  Tile,
  Stack,
  ToastNotification,
} from "@carbon/react";

const Amenities = ({
  propertyUuid = "",
  data = {},
  attributeValues = [],
  formMode = "add",
  currentStep = 4,
  lastSavedStep = 0,
  createProperty,
  saveSectionAttributes,
  onSaveSuccess,
  onComplete,
  onSaved,
  onBack,
  CommonButtons,
  onNavigateNext,
}) => {
  // DEBUG logs...
  console.log('🔍 Amenities (Step 4) received:', {
    formMode,
    currentStep,
    lastSavedStep,
    isEditForm: formMode === "edit" || lastSavedStep >= currentStep,
    hasPropertyUuid: !!propertyUuid,
    hasData: !!data && Object.keys(data).length > 0,
  });

  const isEditForm = useMemo(() => {
    return formMode === "edit" || lastSavedStep >= currentStep;
  }, [formMode, lastSavedStep, currentStep]);

  const FIELD_MAP = useMemo(() => ({
    powerBackup: "com_power_backup",
    lift: "com_lift",
    parking: "com_parking",
    parkingSlots: "com_parking_slots",
    washrooms: "com_washroom",
    waterStorage: "com_water_storage",
    security: "com_security",
    wifi: "wifi",
    propertyCondition: "com_property_condition",
    currentBusiness: "com_business_currently_running",
  }), []);

  const [form, setForm] = useState({
    powerBackup: "",
    lift: "",
    parking: "",
    parkingSlots: "",
    washrooms: "",
    waterStorage: "",
    security: "",
    wifi: "",
    propertyCondition: "",
    currentBusiness: "",
  });

  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const getOptions = useCallback(
    (fieldKey) => {
      const apiKey = FIELD_MAP[fieldKey];
      if (!apiKey) return [];

      return attributeValues
        .filter((a) => a.attrName === apiKey)
        .map((a) => ({
          id: a.id,
          label: a.attr_val_name || a.label || a.value,
        }));
    },
    [attributeValues, FIELD_MAP]
  );

  // Check if parking type requires slots
  const showParkingSlots = useMemo(() => {
    if (!form.parking) return false;
    
    const selectedParking = getOptions("parking").find(
      item => String(item.id) === String(form.parking)
    );
    
    return selectedParking && (
      selectedParking.label.toLowerCase().includes("reserved") || 
      selectedParking.label.toLowerCase().includes("public and reserved")
    );
  }, [form.parking, getOptions]);

  const updateField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    // Mark field as touched when updated
    setTouched((prev) => ({ ...prev, [key]: true }));
  }, []);

  const handleBlur = useCallback((field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  useEffect(() => {
    console.log('Amenities hydration triggered with data:', data);

    if (!data || Object.keys(data).length === 0) return;

    const attributes = data.attributes || {};
    const amenities = data.amenities || {};
    const areaInfo = data.area_information || {};
    const incoming = {};

    Object.entries(FIELD_MAP).forEach(([field, apiKey]) => {
      let raw;
      raw = amenities[apiKey];
      if (raw === undefined || raw === null) {
        raw = attributes[apiKey];
      }
      if (field === "parkingSlots" && (raw === undefined || raw === null)) {
        raw = areaInfo[apiKey];
      }
      if (raw == null) return;
      incoming[field] = raw;
    });

    if (Object.keys(incoming).length > 0) {
      console.log('💧 Hydrating form with:', incoming);
      setForm((prev) => ({ ...prev, ...incoming }));
    }
  }, [data, FIELD_MAP]);

  // VALIDATION - Only washrooms is required
  const getFieldError = useCallback((field) => {
    const v = form[field];

    // Only validate washrooms
    if (field === "washrooms" && !v) {
      return "Washroom(s) is required";
    }

    // parkingSlots validation only when shown
    if (field === "parkingSlots" && showParkingSlots && (!v || Number(v) <= 0)) {
      return "Number of parking slots is required";
    }

    return null;
  }, [form, showParkingSlots]);

  const validateForSave = useCallback(() => {
    // Mark all fields as touched to show validation errors
    const allFields = Object.keys(form);
    const newTouched = {};
    
    allFields.forEach(field => {
      // Mark fields that have validation rules
      if (field === "washrooms" || (field === "parkingSlots" && showParkingSlots)) {
        newTouched[field] = true;
      }
    });
    
    setTouched(prev => ({ ...prev, ...newTouched }));
    
    // Check for errors on required fields
    const errors = [];
    
    // Only washrooms is required now
    if (!form.washrooms) errors.push("Washroom(s) is required");
    
    if (showParkingSlots && (!form.parkingSlots || Number(form.parkingSlots) <= 0)) {
      errors.push("Number of parking slots is required");
    }
    
    return errors;
  }, [form, showParkingSlots]);

  // USE COMMON FIELDS - pass handleBlur
  const { renderDropdown, renderNumberField } = CommonFields({
    form,
    touched,
    getFieldError,
    handleBlur,
    updateField,
    getOptions,
  });

  // FIXED: Save handler with proper validation
  const handleSave = useCallback(async (navigateNext = false) => {
    console.log('💾 Amenities handleSave:', { 
      navigateNext, 
      isEditForm, 
      currentStep,
      hasPropertyUuid: !!propertyUuid,
      formData: form 
    });

    // Run validation and get errors
    const validationErrors = validateForSave();
    
    if (validationErrors.length > 0) {
      setError(validationErrors.join(". "));
      return;
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      let uuid = propertyUuid;

      if (!uuid && createProperty) {
        console.log('🆕 Creating property from Step 4...');
        uuid = await createProperty();
        onSaveSuccess?.(uuid, form, currentStep);
      }

      const payload = {};

      for (const [field, apiKey] of Object.entries(FIELD_MAP)) {
        const v = form[field];
        if (v == null || v === "") continue;

        if (field === "parkingSlots" && !showParkingSlots) continue;

        if (field === "similarPropertiesAvailable") {
          payload[apiKey] = v ? "Yes" : "No";
        }
        else if (field === "parkingSlots") {
          payload[apiKey] = Number(v);
        }
        else {
          payload[apiKey] = v;
        }
      }

      console.log('💾 Saving amenities:', payload);
      await saveSectionAttributes(uuid, payload, form, "amenities");

      setSuccess("Amenities saved successfully!");
      
      onSaveSuccess?.(uuid, form, currentStep);
      onComplete?.(form);

      if (navigateNext) {
        console.log('➡️ Navigating to next step after save');
        onNavigateNext?.(form);
      }
    } catch (err) {
      console.error('❌ Save failed:', err);
      setError(err.message || "Failed to save amenities");
    } finally {
      setSaving(false);
    }
  }, [
    form,
    validateForSave,
    isEditForm,
    currentStep,
    propertyUuid,
    createProperty,
    onSaveSuccess,
    FIELD_MAP,
    saveSectionAttributes,
    onComplete,
    onNavigateNext,
    showParkingSlots
  ]);

  const handleSaveOnly = useCallback((e) => {
    e?.preventDefault();
    handleSave(false);
  }, [handleSave]);

  const handleSaveAndNext = useCallback((e) => {
    handleSave(true);
  }, [handleSave]);

  return (
    <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          subtitle={error}
          timeout={4000}
          onClose={() => setError("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      {success && (
        <ToastNotification
          kind="success"
          title="Success"
          subtitle={success}
          timeout={3000}
          lowContrast
          onClose={() => setSuccess("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      <Grid>
        <Column lg={16}>
          <Tile style={{ padding: "2rem", backgroundColor: "white" }}>
            <Stack gap={6}>
              {/* POWER BACKUP & LIFT */}
              <FormGroup>
                <Grid>
                  <Column lg={8}>
                    {renderDropdown("powerBackup", "Power Backup", false)}
                  </Column>
                  <Column lg={8}>
                    {renderDropdown("lift", "Lift", false)}
                  </Column>
                </Grid>
              </FormGroup>

              {/* PARKING & WASHROOMS */}
              <FormGroup>
                <Grid>
                  <Column lg={8}>
                    {renderDropdown("parking", "Parking", false)}
                    
                    {showParkingSlots && (
                      renderNumberField("parkingSlots", "Parking Slots", "Enter number", true)
                    )}
                  </Column>
                  <Column lg={8}>
                    {renderDropdown("washrooms", "Washroom(s)", true)}
                  </Column>
                </Grid>
              </FormGroup>

              {/* WATER STORAGE & SECURITY */}
              <FormGroup>
                <Grid>
                  <Column lg={8}>
                    {renderDropdown("waterStorage", "Water Storage Facility", false)}
                  </Column>
                  <Column lg={8}>
                    {renderDropdown("security", "Security", false)}
                  </Column>
                </Grid>
              </FormGroup>

              {/* WIFI & PROPERTY CONDITION */}
              <FormGroup>
                <Grid>
                  <Column lg={8}>
                    {renderDropdown("wifi", "WiFi", false)}
                  </Column>
                  <Column lg={8}>
                    {renderDropdown("propertyCondition", "Current Property Condition", false)}
                  </Column>
                </Grid>
              </FormGroup>

              {/* CURRENT BUSINESS & SIMILAR PROPERTIES */}
              <FormGroup>
                <Grid>
                  <Column lg={8}>
                    {renderDropdown("currentBusiness", "What Business is Currently Running?", false)}
                  </Column>
                 
                </Grid>
              </FormGroup>
              
              {/* BUTTONS */}
              {CommonButtons && (
                <CommonButtons
                  onSave={handleSaveOnly}
                  onNext={handleSaveAndNext}
                  showSave
                  showNext
                  isSaveLoading={saving}
                  stepData={form}
                />
              )}
            </Stack>
          </Tile>
        </Column>
      </Grid>
    </div>
  );
};

export default Amenities;